/**
 * @fileoverview Slider - Range slider component
 *
 * Purpose: Select numeric values by dragging a slider
 * Dependencies: Radix UI Slider primitives
 * Used by: Volume controls, price ranges, settings, filters
 *
 * Features:
 * - Drag to select value
 * - Keyboard navigation (arrow keys)
 * - Touch/mobile support
 * - Single or range selection
 * - Accessible (ARIA attributes)
 * - Step support
 *
 * @example
 * <Slider defaultValue={[50]} max={100} step={1} />
 *
 * @example
 * // Range slider
 * <Slider defaultValue={[20, 80]} max={100} step={1} />
 */

import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";

import { cn } from "@/utils/misc";

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>
>(({ className, ...props }, ref) => (
  <SliderPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex w-full touch-none select-none items-center",
      className,
    )}
    {...props}
  >
    <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-secondary">
      <SliderPrimitive.Range className="absolute h-full bg-primary" />
    </SliderPrimitive.Track>
    <SliderPrimitive.Thumb className="block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50" />
  </SliderPrimitive.Root>
));
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
